import { FC, useCallback, useMemo, useState } from 'react'
import { Modal } from '../../components'
import { useTranslation } from 'react-i18next'
import { Button, Grid } from '@mui/material'
import { IdoorAccess, IroomsList } from '../../API/types'
import { useGetRoomsListByRoomsId, UseRoomsLisDelete, UseRoomsListCreate, UseRoomsLisUpdate } from '../../API'
import DoorAccessTable from './DoorAccessTable'
import { useGridApiRef } from '@mui/x-data-grid'
import DoorAccessDelete from './DoorAccessDelete'
import DoorAccessCreate from './DoorAccessCreate'
import DoorAccessLog from './DoorAccessLog'

interface DoorAccessProps {
  handleClickClose: () => void
  roomsList: IroomsList[]
  roomId: number
}

const DoorAccess: FC<DoorAccessProps> = ({ handleClickClose, roomsList, roomId }) => {
  const { t } = useTranslation('')
  const dataGridRef = useGridApiRef()

  const roomsListData = useGetRoomsListByRoomsId(roomId.toString())

  const selectedRoomsData = useMemo(() => {
    return roomsList.find((roomsData) => roomsData.id === roomId)
  }, [roomsList, roomId])

  const [openDelete, setOpenDelete] = useState<string | null>(null)
  const [openCreate, setOpenCreate] = useState<string | null>(null)
  const [openLog, setOpenLog] = useState<string | null>(null)

  const handleClose = useCallback(() => {
    handleClickClose()
  }, [handleClickClose])

  const handleAccept = useCallback(async () => {
    handleClickClose()
  }, [handleClickClose])

  const handleCreateDoorAccess = useCallback(
    async (rawDoorAccess: IdoorAccess) => {
      const roomsData: IdoorAccess = {
        roomsListId: roomId.toString(),
        dateFrom: rawDoorAccess.dateFrom,
        dateTo: rawDoorAccess.dateTo,
        disabled: rawDoorAccess.disabled,
        comments: rawDoorAccess.comments,
        pinList: rawDoorAccess.pinList,
        nfcList: rawDoorAccess.nfcList,
      }

      if (rawDoorAccess.id === '0') {
        // creat new
        const newDoorAccess = await UseRoomsListCreate(roomsData)
        dataGridRef.current.updateRows([newDoorAccess])
      } // update existing
      else {
        roomsData.id = rawDoorAccess.id
        const selectedRoomsData = roomsListData.find((roomsData) => roomsData.id === rawDoorAccess.id)
        if (selectedRoomsData) {
          const updatedDoorAccess = await UseRoomsLisUpdate(roomsData)

          selectedRoomsData.dateFrom = updatedDoorAccess.dateFrom
          selectedRoomsData.dateTo = updatedDoorAccess.dateTo
          selectedRoomsData.disabled = updatedDoorAccess.disabled
          selectedRoomsData.comments = updatedDoorAccess.comments
          selectedRoomsData.pinList = updatedDoorAccess.pinList
          selectedRoomsData.nfcList = updatedDoorAccess.nfcList

          dataGridRef.current.updateRows([selectedRoomsData])
        }
      }
    },
    [roomId, dataGridRef, roomsListData],
  )

  const handleDeleteDoorAccess = useCallback(
    async (id: string) => {
      dataGridRef.current.updateRows([{ id: id, _action: 'delete' }])

      await UseRoomsLisDelete(id)
    },
    [dataGridRef],
  )

  const handleClickOpenDelete = useCallback((id: string) => {
    setOpenDelete(id)
  }, [])

  const handleClickCloseDelete = useCallback(() => {
    setOpenDelete(null)
  }, [])

  const handleClickOpenCreate = useCallback((id: string) => {
    setOpenCreate(id)
  }, [])

  const handleClickCloseCreate = useCallback(() => {
    setOpenCreate(null)
  }, [])

  const handleClickOpenLog = useCallback((id: string) => {
    setOpenLog(id)
  }, [])

  const handleClickCloseLog = useCallback(() => {
    setOpenLog(null)
  }, [])

  return (
    <Modal
      isOpen={true}
      maxWidth={'md'}
      handleClose={handleClose}
      handleAccept={handleAccept}
      dialogTitle={t('Durvju pieeja ') + '(' + selectedRoomsData?.roomName + ')'}
      closeTitle={t('Aizvērt')}
    >
      <Grid container spacing={0} sx={{ background: '#121212', paddingTop: 1 }}>
        <Grid item xs={12} sx={{}}>
          <DoorAccessTable
            roomsListData={roomsListData}
            dataGridRef={dataGridRef}
            handleEditDoorAccess={handleClickOpenCreate}
            handleClickOpenDelete={handleClickOpenDelete}
            handleClickOpenLog={handleClickOpenLog}
          />
          <Button
            variant='outlined'
            color='success'
            style={{ margin: '4px 0 0px 0px' }}
            onClick={() => handleClickOpenCreate('0')}
          >
            {t('Create Door Access')}
          </Button>
        </Grid>
      </Grid>

      {openDelete && (
        <DoorAccessDelete
          handleClickClose={handleClickCloseDelete}
          handleDeleteDoorAccess={handleDeleteDoorAccess}
          roomsListData={roomsListData}
          id={openDelete}
        />
      )}

      {openCreate && (
        <DoorAccessCreate
          handleClickClose={handleClickCloseCreate}
          createDoorAccess={handleCreateDoorAccess}
          roomsListData={roomsListData}
          id={openCreate}
        />
      )}

      {openLog && (
        <DoorAccessLog
          handleClickClose={handleClickCloseLog}
          roomsListData={roomsListData}
          roomsList={roomsList}
          id={openLog}
        />
      )}
    </Modal>
  )
}

export default DoorAccess
